import { useEffect, useRef, memo } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { type FunctionDeclaration, SchemaType } from "@google/generative-ai";

interface AltairProps {
  addNotes: (note: string) => void;
  createSummary: (summary: {
    caseType: string;
    ipcSections: string;
    nyayaSections: string;
    legalAngles: string;
  }) => void;
}

const declarations: FunctionDeclaration[] = [{
  name: "addNotes",
  parameters: {
    type: SchemaType.OBJECT,
    properties: {
      text: {
        type: SchemaType.STRING,
        description: "The text to add to the notes",
      },
    },
    required: ["text"],
  },
  description: "Keep track of notes",
},
{
  name: "createSummary",
  parameters: {
    type: SchemaType.OBJECT,
    properties: {
      caseType: {
        type: SchemaType.STRING,
        description: "Type of the case",
      },
      ipcSections: {
        type: SchemaType.STRING,
        description: "IPC sections involved",
      },
      nyayaSections: {
        type: SchemaType.STRING,
        description: "Nyaya sections involved",
      },
      legalAngles: {
        type: SchemaType.STRING,
        description: "Legal angles involved",
      },
    },
    required: ["caseType", "ipcSections", "nyayaSections", "legalAngles"],
  },
  description: "create summary of the case",
}];

function AltairComponent({ addNotes, createSummary }: AltairProps) {
  const { client, setConfig } = useLiveAPIContext();

  useEffect(() => {
    setConfig({
      model: "models/gemini-2.5-flash-native-audio-preview-09-2025",
      generationConfig: {
        responseModalities: "audio",
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: "Kore" } },
        },
      },
      systemInstruction: {
        parts: [
          {
            text: 'You are an Indian lawyer. Talk in English. Listen to clients and take notes of all the conversation. You answer questions and provide legal advice based on the conversation. Add all the details the notes and at the end make a summary with possible legal angles. Add Bharatiya samhita section, IPC section in summary. You help senior lawyer by collecting all the information. You name is Bhashini from firm ALMT Legal.',
          },
        ],
      },
      tools: [
        { functionDeclarations: declarations },
      ],
    });
  }, [setConfig]);

  useEffect(() => {
    if (!client) return;

    const onToolCall = (event: any) => {
      const functionCall = event.functionCalls[0];
      if (!functionCall) return;

      switch (functionCall.name) {
        case "addNotes":
          const text = functionCall.args?.text;
          if (text) addNotes(text);
          break;
        case "createSummary":
          const args = functionCall.args;
          if (args) {
            createSummary({
              caseType: args.caseType || "",
              ipcSections: args.ipcSections || "",
              nyayaSections: args.nyayaSections || "",
              legalAngles: args.legalAngles || "",
            });
          }
          break;
      }
    };

    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client, addNotes, createSummary]);

  const embedRef = useRef<HTMLDivElement>(null);

  return <div className="vega-embed" ref={embedRef} />;
}

export const Altair = memo(AltairComponent);
